// Dashboard.js - the org at a glance: its plans and members, what to do next, and recent activity. Built from the
// logistics-workflow page templates (PageLayout, StatGrid/StatCard, EmptyState).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, CheckCircle2, Circle, Layers, Share2, Users } from "lucide-react";
import PageLayout from "./common/PageLayout";
import PageSection from "./common/PageSection";
import SkeletonLoader from "./common/SkeletonLoader";
import StatCard from "./common/StatCard";
import StatGrid from "./common/StatGrid";
import { listPlans } from "../game-builder/api/gameBuilderAPI";
import { listAuditEvents, listMembers } from "../api/studioAPI";
import { useProfile, isOwner } from "../context/ProfileContext";
import { colors, surfaces, typography, buttons } from "../styles/index";

const ACTIONS = { CREATE: "created", DELETE: "deleted", PUBLISH: "published", UNPUBLISH: "unpublished", INVITE: "invited",
  JOIN: "joined", ROLE: "changed the role of", REMOVE: "removed", UPDATE: "updated" };

export const describe = (e) => {
  const who = (e.actor || "someone").split("@")[0];
  const what = { plan: "plan", game: "game", member: "", org: "the organization" }[e.entity_type] ?? e.entity_type;
  return `${who} ${ACTIONS[e.action] || e.action.toLowerCase()} ${what} ${e.entity_type === "org" ? "" : e.entity_id}`.replace(/\s+/g, " ").trim();
};
export const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

export default function Dashboard() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([listPlans(), listMembers(), listAuditEvents(50).catch(() => ({ items: [] }))])
      .then(([plans, members, audit]) => setData({ plans: plans.plans, members: members.items, events: audit.items }))
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <p role="alert" style={{ color: colors.danger }}>{error}</p>;
  if (!data) return <SkeletonLoader type="dashboard" count={3} />;

  const missions = data.plans.reduce((n, p) => n + (p.missions || 0), 0);
  const published = new Set(data.events.filter((e) => e.action === "PUBLISH").map((e) => e.entity_id)).size;
  const steps = [
    { done: data.plans.length > 0, label: "Make a plan from one of your procedures", go: () => navigate("/app/studio") },
    { done: published > 0, label: "Build a mission's game and share its link", go: () => navigate("/app/studio") },
    ...(isOwner(profile) && !profile?.org?.is_demo
      ? [{ done: data.members.length > 1, label: "Invite a colleague", go: () => navigate("/app/settings?section=members") }]
      : []),
  ];

  return (
    <PageLayout title={profile?.org?.org_name || "Dashboard"}>
      <StatGrid>
        <StatCard label="Plans" value={data.plans.length} icon={BookOpen} onClick={() => navigate("/app/studio")} />
        <StatCard label="Missions" value={missions} icon={Layers} onClick={() => navigate("/app/studio")} />
        <StatCard label="Games shared" value={published} icon={Share2} />
        <StatCard label="Members" value={data.members.length} icon={Users} onClick={() => navigate("/app/settings?section=members")} />
      </StatGrid>

      {steps.some((s) => !s.done) && (
        <PageSection title="Getting started">
          <div style={{ ...surfaces.card, textAlign: "left" }}>
            {steps.map((s) => (
              <button key={s.label} type="button" onClick={s.go}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 4px", border: "none",
                  background: "none", cursor: "pointer", textAlign: "left", color: s.done ? colors.textMuted : colors.text,
                  textDecoration: s.done ? "line-through" : "none", fontSize: "0.95rem" }}>
                {s.done ? <CheckCircle2 size={18} color={colors.accent} /> : <Circle size={18} color={colors.textMuted} />}
                {s.label}
              </button>
            ))}
          </div>
        </PageSection>
      )}

      <PageSection title="Recent plans">
        {data.plans.length === 0 ? (
          <div style={{ ...surfaces.card, textAlign: "center" }}>
            <p style={typography.body}>No plans yet. Start one from an SOP, a manual or a policy.</p>
            <button type="button" style={buttons.primary} onClick={() => navigate("/app/studio")}>Open the Studio</button>
          </div>
        ) : (
          <div style={{ ...surfaces.card, textAlign: "left", padding: 0 }}>
            {data.plans.slice(0, 5).map((p) => (
              <button key={p.id} type="button" onClick={() => navigate("/app/studio")}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", border: "none",
                  borderBottom: `1px solid ${colors.border}`, background: "none", cursor: "pointer" }}>
                <strong>{p.goal || p.id}</strong>
                <div style={{ ...typography.small, color: colors.textMuted }}>
                  {p.missions} mission{p.missions === 1 ? "" : "s"} · {when(p.created_at)}{p.created_by ? ` · ${p.created_by}` : ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </PageSection>

      {data.events.length > 0 && (
        <PageSection title="Recent activity">
          <div style={{ ...surfaces.card, textAlign: "left" }}>
            {data.events.slice(0, 8).map((e) => (
              <div key={`${e.at}-${e.entity_id}-${e.action}`} style={{ display: "flex", justifyContent: "space-between", gap: 12,
                padding: "6px 0", borderBottom: `1px solid ${colors.border}`, fontSize: "0.9rem", flexWrap: "wrap" }}>
                <span>{describe(e)}</span>
                <span style={{ color: colors.textMuted }}>{when(e.at)}</span>
              </div>
            ))}
          </div>
        </PageSection>
      )}
    </PageLayout>
  );
}
